import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProgressBarService } from '@services/progress-bar.service';
import { AlertModalComponent, modalTypes } from '@shared/components/modals/alert-modal/alert-modal.component';
import { isPeri } from '@shared/extensions/extensions';
import { pushGtmTag } from '@shared/extensions/gtm.extentions';
import { RegistrationModel } from '@shared/extensions/user-data-extensions';
import { ProfileStepCode } from '@shared/models/profile-step-code';
import { LabelTextPipe } from '@shared/pipes/label-text.pipe';
import { Profile } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { ClientFacade } from '@store/client-store/client.facade';
import { hasPartnerCodeValidationError } from '@store/client-store/client.helpers';
import { ClientAuthorizationStatus, ClientType } from '@store/client-store/client.models';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { AdvertisementSourceClientComponent } from './components/advertisement-source-client/advertisement-source-client.component';
import { RegistrationFormComponent } from './components/registration-form/registration-form.component';

@Component({
  standalone: false,
  selector: 'app-membership-with-otp-check',
  templateUrl: './membership-with-otp-check.component.html',
  styleUrls: ['./membership-with-otp-check.component.scss']
})
export class MembershipWithOtpCheckComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(AdvertisementSourceClientComponent) advertisementSourceClient?: AdvertisementSourceClientComponent;
  @ViewChild(RegistrationFormComponent) registrationFormComponent?: RegistrationFormComponent;

  blockedProfile = false;
  clientType: ClientType | null = null;
  authorized = false;
  isPeri = isPeri();
  clientProfile: Profile | null = null;
  isSubmittingRegistration = false;
  queryParams: Params = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bsModalService: BsModalService,
    private clientFacade: ClientFacade,
    private customerService: CustomerService,
    private progressBarService: ProgressBarService,
    private labelTextPipe: LabelTextPipe
  ) {}

  ngOnInit(): void {
    this.progressBarService.sendStep(0);
    this.initQueryParamsSubscription();
    this.initClientTypeSubscription();
    this.initClientAuthorizationSubscriptions();
  }

  private initQueryParamsSubscription(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams: Params) => {
      this.queryParams = queryParams;
    });
  }

  private initClientTypeSubscription(): void {
    this.clientFacade.clientType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(clientType => {
      this.clientType = clientType;
    });
  }

  private initClientAuthorizationSubscriptions(): void {
    combineLatest([this.clientFacade.authorizedClientProfile$, this.clientFacade.clientAuthorizationStatus$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([profile, status]) => {
        this.clientProfile = profile;

        switch (status) {
          case ClientAuthorizationStatus.Authorized:
            if (this.isProspect) {
              this.blockedProfile = false;
              return;
            }

            if (!profile) {
              return;
            }

            this.blockedProfile = false;
            this.authorized = true;
            return;
          case ClientAuthorizationStatus.Blocked:
            this.authorized = false;
            this.blockedProfile = true;
            return;
          case ClientAuthorizationStatus.Unauthorized:
            this.blockedProfile = false;
            this.authorized = false;
            this.showInvalidModal();
            return;
          default:
            this.blockedProfile = false;
            if (status !== ClientAuthorizationStatus.Authorizing) {
              this.authorized = false;
            }
        }
      });

    this.clientFacade.clientAuthorizationStatus$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(status => {
        if (status === ClientAuthorizationStatus.Unauthorized) {
          this.showInvalidModal();
        }
      });
  }

  private showInvalidModal(): void {
    this.bsModalService.show(AlertModalComponent, {
      initialState: {
        type: modalTypes.error,
        title: this.labelTextPipe.transform('HOME.ErrorClientNotFound')
      },
      class: 'modal-lg'
    });
  }

  private async checkAndSaveUtms(): Promise<void> {
    if (!this.queryParams) {
      return;
    }

    const utmParams = Object.keys(this.queryParams).filter((key: string) => key.indexOf('utm') !== -1);

    if (utmParams.length === 0) {
      return;
    }

    let utmsString = '';

    utmParams.forEach((utm: string, index: number) => {
      utmsString += `${utm}=${this.queryParams[utm]}`;

      if (index !== utmParams.length - 1) {
        utmsString += '&';
      }
    });

    await this.customerService.updateUTMData(utmsString);
  }

  async onSubmitRegistration(model: RegistrationModel): Promise<void> {
    if (this.isSubmittingRegistration) {
      return;
    }

    if (this.advertisementSourceClient && !this.advertisementSourceClient.isMifFormValid) {
      this.advertisementSourceClient.markAllAsTouched();
      return;
    }

    this.isSubmittingRegistration = true;

    try {
      const userId = await this.customerService.updateRegistrationModel({
        ...model,
        isMember: this.isContract
      } as RegistrationModel);

      await this.finalizeAuthorizedRegistration(userId);
    } catch (error) {
      if (!hasPartnerCodeValidationError(error)) {
        throw error;
      }

      this.advertisementSourceClient?.markPartnerCodeInvalid();
    } finally {
      this.isSubmittingRegistration = false;
    }
  }

  async onProspectRegistrationCompleted(userId: string): Promise<void> {
    if (this.isSubmittingRegistration) {
      return;
    }

    this.isSubmittingRegistration = true;

    try {
      await this.finalizeAuthorizedRegistration(userId, false);
    } finally {
      this.isSubmittingRegistration = false;
    }
  }

  private async finalizeAuthorizedRegistration(
    userId: string,
    saveAdvertisementSourceData: boolean = true
  ): Promise<void> {
    pushGtmTag(ProfileStepCode.MembershipCheck);
    await this.checkAndSaveUtms();

    if (saveAdvertisementSourceData) {
      await this.advertisementSourceClient?.saveAdvertisementSourceData();
    }

    await this.router.navigate(['/residence-questions'], {
      queryParams: { userId }
    });
  }

  get isContract(): boolean {
    return this.clientType === ClientType.Contract;
  }

  get isProspect(): boolean {
    return this.clientType === ClientType.Prospect;
  }
}
